import React from "react";
import { motion } from "framer-motion";
import { WHY } from "./siteData";
import { useAdminImages, ChangeBtn } from "./AboutPremium";

const EASE = [0.22, 1, 0.36, 1];
const PATS = [
  "repeating-linear-gradient(45deg,#1f5f78 0 14px,#a8382f 14px 28px,#c98f2e 28px 42px)",
  "radial-gradient(circle at 12px 12px,#f2c14e 0 4px,transparent 5px) 0 0/24px 24px,#24406a",
  "repeating-linear-gradient(90deg,#c97bb2 0 12px,#b25f9a 12px 24px)",
  "radial-gradient(circle at 50% 50%,#e9d7b9 0 6px,transparent 7px) 0 0/28px 28px,#8a5a32",
  "repeating-linear-gradient(0deg,#2f6b4f 0 10px,#e8d9b5 10px 14px,#2f6b4f 14px 28px)",
  "conic-gradient(from 45deg,#b5543a 25%,#f0e0c8 0 50%,#b5543a 0 75%,#f0e0c8 0) 0 0/26px 26px",
  "repeating-linear-gradient(-45deg,#1a1a1a 0 8px,#c9a27a 8px 16px)",
  "radial-gradient(circle at 0 0,#d9a441 0 10px,transparent 11px) 0 0/22px 22px,radial-gradient(circle at 11px 11px,#7a2f2a 0 5px,transparent 6px) 0 0/22px 22px,#f3e3c3",
];

const CSS = `
@import url('https://fonts.googleapis.com/css2?family=Pinyon+Script&display=swap');
@keyframes wr-sway{0%,100%{transform:rotate(-2.2deg)}50%{transform:rotate(2.2deg)}}
.wr-sw{transform-origin:50% 0;animation:wr-sway 5s ease-in-out infinite;perspective:900px}
.wr-inner{position:relative;height:330px;transition:transform .9s cubic-bezier(.2,.8,.2,1);transform-style:preserve-3d}
.wr-hang:hover .wr-inner{transform:rotateY(180deg)}
.wr-admin .wr-hang:hover .wr-inner{transform:none}
.wr-face,.wr-back{position:absolute;inset:0;backface-visibility:hidden;-webkit-backface-visibility:hidden;border-radius:2px 2px 6px 6px;box-shadow:0 22px 40px rgba(43,29,22,.22)}
.wr-face{background-size:cover;background-position:center;clip-path:polygon(0 0,100% 0,100% 94%,92% 100%,84% 94%,76% 100%,68% 94%,60% 100%,52% 94%,44% 100%,36% 94%,28% 100%,20% 94%,12% 100%,4% 94%,0 100%)}
.wr-back{transform:rotateY(180deg);background:#2b1d16;color:#FAF9F6;padding:26px 18px}
.wr-back::before{content:"";position:absolute;inset:7px;border:1px dashed rgba(232,200,160,.45);pointer-events:none}
@keyframes wr-shine{0%{background-position:150% 0}100%{background-position:-50% 0}}
.wr-shine{display:inline-block;padding:0 .18em;background:linear-gradient(110deg,#8a5a32 35%,#f3d6a4 50%,#8a5a32 65%);background-size:250% 100%;-webkit-background-clip:text;background-clip:text;color:transparent;animation:wr-shine 3.5s linear infinite}
@media (prefers-reduced-motion:reduce){.wr-sw,.wr-shine{animation:none}}
`;

export default function WhyRail() {
  const { isAdmin, src, reload } = useAdminImages();
  return (
    <section className={`relative overflow-hidden bg-[#FAF9F6] py-16 lg:py-20 ${isAdmin ? "wr-admin" : ""}`} data-testid="why-rail">
      <style>{CSS}</style>
      <div className="mx-auto max-w-[1400px] px-6 lg:px-12">
        <div className="flex flex-col gap-6">
          <div>
            <motion.span initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.8 }}
              className="inline-flex items-center gap-4 text-[0.7rem] uppercase tracking-[0.32em] text-[#8a5a32]">
              <span className="h-px w-12 bg-[#8a5a32]" />Why Nayara Studio
            </motion.span>
            <motion.h2 initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 1, delay: 0.15, ease: EASE }}
              className="mt-6 font-display text-[clamp(2.4rem,5vw,4.5rem)] font-light leading-[1.05] tracking-tight text-[#1A1A1A]">
              A manufacturing partner<br />you can{" "}
              <span className="wr-shine" style={{ fontFamily: "'Pinyon Script', cursive", fontSize: "1.3em" }}>trust.</span>
            </motion.h2>
          </div>
          <motion.p initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.9, delay: 0.5 }}
            className="max-w-xl text-[1.05rem] leading-[1.85] text-[#595959]">
            Eight reasons brands keep coming back — each one cut from the same cloth.
          </motion.p>
        </div>

        {/* Rail */}
        <motion.div initial={{ scaleX: 0 }} whileInView={{ scaleX: 1 }} viewport={{ once: true }} transition={{ duration: 1.4, ease: EASE }}
          className="mt-20 h-2.5 origin-left rounded-full bg-gradient-to-b from-[#a98a66] to-[#6e5134] shadow-[0_6px_14px_rgba(43,29,22,.2)]" />

        <div className="-mt-1.5 grid grid-cols-2 gap-x-4 gap-y-10 sm:grid-cols-4 lg:grid-cols-8">
          {WHY.slice(0, 8).map((w, i) => {
            const img = src(`why${i + 1}`, null);
            return (
              <motion.div key={w.title} className="wr-hang relative flex flex-col items-center"
                initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
                transition={{ duration: 0.9, delay: 0.3 + i * 0.08, ease: EASE }}>
                <svg width="30" height="46" viewBox="0 0 30 46" className="-mb-0.5 block" aria-hidden="true">
                  <path d="M15 46V30M15 30C4 30 4 14 15 14s11-9 3-12" fill="none" stroke="#8a6a4a" strokeWidth="2" />
                </svg>
                <div className="wr-sw w-full" style={{ animationDelay: `-${i * 0.7}s` }}>
                  <div className="wr-inner">
                    <div className="wr-face" style={img ? { backgroundImage: `url(${img})` } : { background: PATS[i % PATS.length] }}>
                      <div className="absolute inset-x-2.5 bottom-[30px] border-t-2 border-[#8a5a32] bg-[#FAF9F6] px-3 pb-3.5 pt-3">
                        <b className="block text-[10px] font-normal tracking-[0.3em] text-[#B89B72]">0{i + 1}</b>
                        <span className="mt-1.5 block font-display text-[19px] leading-tight text-[#1A1A1A]">{w.title}</span>
                      </div>
                    </div>
                    <div className="wr-back">
                      <b className="text-[10px] font-normal tracking-[0.3em] text-[#e8c8a0]">0{i + 1}</b>
                      <h4 className="mb-3 mt-3.5 font-display text-2xl leading-tight">{w.title}</h4>
                      <p className="text-[13.5px] font-light leading-relaxed text-[#d9c6b0]">{w.text}</p>
                    </div>
                  </div>
                </div>
                {isAdmin && <div className="absolute inset-x-0 top-14 z-20"><ChangeBtn slot={`why${i + 1}`} onDone={reload} /></div>}
              </motion.div>
            );
          })}
        </div>

        <p className="mt-14 text-center text-4xl text-[#B89B72]" style={{ fontFamily: "'Pinyon Script', cursive" }}>
          {isAdmin ? "Admin: tap Change on any swatch" : "hover a swatch to turn it over"}
        </p>
      </div>
    </section>
  );
}
